import React, { useMemo, useState } from 'react';
import { CategoryTag } from '../catalog-list/category-tag';
import { camelCase, isNumber, startCase } from 'lodash';
import {
  ProductDetails,
  ProductFacetMap,
  PricingStrategy,
  ProductSegmentMetric,
} from '@zonofi/zono-money-store';
import './product-details-content.scss';
import { SVGS, SegmentTag } from '@zonofi/zono-money-design-system';
import { ProductDetailsFacets } from '../product-details-facets/product-details-facets';
import { getFormattedAmount } from '@zonofi/common';
import { WeightedPriceInfo } from '../weighted-price-info/weighted-price-info';

interface ProductDetailsContentProps {
  productDetails: ProductDetails;
  hidePrice: boolean;
  hideImages: boolean;
  facets: ProductFacetMap;
  onSelectFacet: (facetCode: any, facetValue: any) => void;
  showOutOfStack: boolean;
  outOfStockFacetValueMap: Record<string, Record<string, boolean>>;
}
export const ProductDetailsContent: React.FC<ProductDetailsContentProps> = ({
  productDetails,
  hidePrice,
  hideImages,
  facets,
  onSelectFacet,
  showOutOfStack,
  outOfStockFacetValueMap,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Truncate the description to 300 characters
  const truncatedDescription = useMemo(
    () =>
      productDetails?.description?.length > 300
        ? productDetails?.description?.slice(0, 300) + '...'
        : productDetails?.description,
    [productDetails]
  );
  const isPtrGraterThanOne = useMemo(
    () =>
      isNumber(Number(productDetails?.details?.ptr)) &&
      Number(productDetails?.details?.ptr) > 1,
    [productDetails]
  );
  const toggleReadMore = () => {
    setIsExpanded(!isExpanded);
  };
  const formatTitle = (input: string) => {
    if (/[A-Z]/.test(input)) return input;

    // Otherwise, convert each space-separated word to Title Case.
    return input
      ?.split(' ')
      ?.map(
        (word) => word?.charAt(0).toUpperCase() + word?.slice(1).toLowerCase()
      )
      .join(' ');
  };
  return (
    <div className={'product-details-content'}>
      <div
        className={`product-details-content-info ${
          hideImages ? 'append-margin' : ''
        }`}
      >
        <div className="product-title-description">
          <div className="product-header">
            <div className="product-details-categories-container">
              <div className="product-details-categories">
                {productDetails?.segments
                  ?.filter(
                    (x) =>
                      x?.segmentName === ProductSegmentMetric.FAST_MOVING ||
                      x?.segmentName === ProductSegmentMetric.NEW_LAUNCH
                  )
                  ?.map((item, index) =>
                    item ? (
                      <SegmentTag key={index} mainText={item.segmentName} />
                    ) : null
                  )}
              </div>
              <div className="product-details-categories">
                {productDetails?.tags.map((category, index) => (
                  <CategoryTag key={index} category={category} />
                ))}
              </div>
            </div>
            <div className="product-title font-primary-semibold">
              {formatTitle(productDetails?.title)}
            </div>

            {productDetails?.promotions &&
              productDetails?.promotions?.length !== 0 && (
                <div className="catalog-list-item-promotion">
                  <img src={SVGS.DiscountIcon} alt="discount" />
                  <span className="catalog-list-item-promotion-text font-primary-semibold">
                    {productDetails?.promotions?.[0]?.name}
                    {/* {productDetails?.promotions?.[0]?.promotionType === SchemeTypes.BUYXGETY
                    ? `Buy ${productDetails?.promotions?.[0]?.minQtyASC} Qty, get ${productDetails?.promotions?.[0]?.freeQtyASC} free`
                    : `Buy ${productDetails?.promotions?.[0]?.minQtyASC} Qty, get ${productDetails?.promotions?.[0]?.discount} % off`} */}
                  </span>
                </div>
              )}
            <div className="product-price font-primary-bold">
              {hidePrice
                ? '-'
                : productDetails?.price
                  ? getFormattedAmount(productDetails?.price)
                  : '-'}
            </div>
            {productDetails?.details?.pricingStrategy ===
              PricingStrategy.WEIGHT_BASED && (
              <WeightedPriceInfo
                weightedPrice={productDetails?.details?.weightedPrice}
              />
            )}
            {showOutOfStack &&
              productDetails?.stock < 1 &&
              Object.keys(facets)?.length === 0 && (
                <div className="catalog-list-out-of-stack-tag">
                  <span className="catalog-list-out-of-stack-tag-text font-primary">
                    OUT OF STOCK
                  </span>
                </div>
              )}
          </div>

          {Object.keys(facets)?.length !== 0 && (
            <ProductDetailsFacets
              facets={facets}
              onSelectFacet={onSelectFacet}
              showOutOfStock={showOutOfStack}
              outOfStockFacetValueMap={outOfStockFacetValueMap}
            />
          )}

          <div className="description">
            <div className="description-header font-primary-semibold">
              DESCRIPTION
            </div>
            <div className="font-primary product-description">
              {isExpanded ? productDetails?.description : truncatedDescription}
              {productDetails?.description?.length > 300 && (
                <span
                  onClick={toggleReadMore}
                  className="read-more font-primary"
                >
                  {isExpanded ? 'Read Less' : 'Read More'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* <div className="product-other-details">
          <div className="other-details-header font-primary-semibold">
            OTHER DETAILS
          </div>
          <div className="other-details-wrapper">
            {!hidePrice && isPtrGraterThanOne && (
              <div className="details-row-top">
                <div className="font-primary price-container">
                  <span className="font-primary price-category">PTR</span>
                  <span className="value font-primary-semibold">
                    {getFormattedAmount(Number(productDetails?.details?.ptr))}
                  </span>
                </div>
                <div className="price-container">
                  <span className="font-primary price-category">Margin</span>
                  <span className="value font-primary-semibold">
                    {getFormattedAmount(
                      Number(productDetails?.details?.margin)
                    )}
                  </span>
                </div>
              </div>
            )}
            <div className="details-row-bottom">
              <div className="price-container">
                <span className="font-primary price-category">MOQ</span>
                <span className="value font-primary-semibold">
                  {productDetails?.details?.moq}
                </span>
              </div>
            </div>
          </div>
        </div> */}
      </div>
    </div>
  );
};
